import { Typography } from "@lyfegen-platform/react-common";
import { Box, Theme } from "@material-ui/core";
import { PaletteOptions } from "@material-ui/core/styles/createPalette";
import { TFunction } from "i18next";
import { IPalette } from "libs/react-common/src/theme/newLyfeTheme";
import { FlexFormStatus as NewFlexFormStatus, RefundStatus as NewRefundStatus } from "../../../models/generated/graphql";
import { CaseStatus, ContractStatus, DocumentStatus, FlexFormStatus, RefundStatus } from "../../../types/graphql-global-types";
import { PublicAgreementStatus, QuestFilterStatus } from "../../filters/mappings";
import FlexTypographyComponent from "../../FlexTypographyComponent";
import Approved from "../../icons/Status/Agreements/Approved";
import InApproval from "../../icons/Status/Agreements/InApproval";
import Rejected from "../../icons/Status/Agreements/Rejected";
import Running from "../../icons/Status/Agreements/Running";
import Active from "../../icons/Status/Cases/Active";
import InReview from "../../icons/Status/Cases/InReview";
import Suspended from "../../icons/Status/Cases/Suspended";
import Tentative from "../../icons/Status/Cases/Tentative";
import Issued from "../../icons/Status/Documents/Issued";
import WorkingDraft from "../../icons/Status/Documents/WorkingDraft";
import Draft from "../../icons/Status/Forms/Draft";
import Submitted from "../../icons/Status/Forms/Submitted";
import { Resolved, Unresolved } from "../../icons/Status/Quests/QuestStatusIcons";
import Invoiced from "../../icons/Status/Refunds/Invoiced";
import New from "../../icons/Status/Refunds/New";
import Pending from "../../icons/Status/Refunds/Pending";
import Revoked from "../../icons/Status/Refunds/Revoked";
import Settled from "../../icons/Status/Refunds/Settled";
import { EligibilityStatus } from "@lyfegen-platform/graphql-schema/models";

export enum TransactionStatus {
	IN_REVIEW = "IN_REVIEW",
	INELIGIBLE = "ineligible",
	ELIGIBLE = "eligible",
}

export const agreementStatusToColor = (status: ContractStatus, palette: Theme["palette"], isText: boolean = false): string => {
	switch (status.toUpperCase()) {
		case ContractStatus.APPROVED:
			return isText ? palette["lightGreen"].main : palette["lightGreen"].light!;
		case ContractStatus.IN_APPROVAL:
			return isText ? palette["bubbles"].main : palette["bubbles"].light!;
		case ContractStatus.REJECTED:
			return isText ? palette["seashell"].main : palette["seashell"].light!;
		case ContractStatus.RUNNING:
			return isText ? palette["lavender"].main : palette["lavender"].light!;
		default:
			console.error("unknown agreement status");
			return isText ? palette["lavender"].main : palette["lavender"].light!;
	}
};

export const getAgreementStatusIcon = (status: ContractStatus): JSX.Element => {
	switch (status) {
		case ContractStatus.APPROVED:
			return <Approved />;
		case ContractStatus.IN_APPROVAL:
			return <InApproval />;
		case ContractStatus.REJECTED:
			return <Rejected />;
		case ContractStatus.RUNNING:
			return <Running />;
		default:
			return <InApproval />;
	}
};

export const caseStatusToColor = (status: CaseStatus, palette: Theme["palette"], isText: boolean = false): string => {
	switch (status.toUpperCase()) {
		case CaseStatus.TENTATIVE:
			return isText ? palette["whiteChocolate"].main : palette["whiteChocolate"].light!;
		case CaseStatus.IN_REVIEW:
			return isText ? palette["bubbles"].main : palette["bubbles"].light!;
		case CaseStatus.SUSPENDED:
			return isText ? palette["brightGray"].main : palette["brightGray"].light!;
		case CaseStatus.ACTIVE:
			return isText ? palette["lavender"].main : palette["lavender"].light!;
		default:
			console.error("unknown case status");
			return isText ? palette["lavender"].main : palette["lavender"].light!;
	}
};

export const getCaseStatusIcon = (status: CaseStatus): JSX.Element => {
	switch (status) {
		case CaseStatus.ACTIVE:
			return <Active />;
		case CaseStatus.IN_REVIEW:
			return <InReview />;
		case CaseStatus.TENTATIVE:
			return <Tentative />;
		case CaseStatus.SUSPENDED:
			return <Suspended />;
		default:
			return <InReview />;
	}
};

export const formStatusToColor = (
	status: FlexFormStatus | "due" | "overdue" | undefined,
	palette: Theme["palette"],
	isText: boolean = false
): string => {
	switch (status?.toUpperCase()) {
		case FlexFormStatus.DRAFT:
			return isText ? palette["cornSilk"].main : palette["cornSilk"].light!;
		case FlexFormStatus.SUBMITTED:
			return isText ? palette["lightGreen"].main : palette["lightGreen"].light!;
		case FlexFormStatus.MATCHED:
			return isText ? palette["lightGreen"].main : palette["lightGreen"].light!;
		case FlexFormStatus.UNMATCHED:
			return isText ? palette["seashell"].main : palette["seashell"].light!;
		default:
			console.error("unknown case status");
			return isText ? palette["lavender"].main : palette["lavender"].light!;
	}
};

export const getFormStatusIcon = (status: FlexFormStatus | "due" | "overdue"): JSX.Element => {
	switch (status) {
		case FlexFormStatus.SUBMITTED:
			return <Submitted />;
		case FlexFormStatus.DRAFT:
			return <Draft />;
		case "due":
			return <Draft />;
		default:
			return <InReview />;
	}
};

export const getRecordStatusCaseDetailsColorsAndIcons = (
	status: FlexFormStatus | RefundStatus | NewRefundStatus | NewFlexFormStatus | TransactionStatus | EligibilityStatus,
	palette: PaletteOptions
): [IPalette, JSX.Element] => {
	switch (status) {
		case FlexFormStatus.MATCHED:
			return [palette["lightGreen"], <Invoiced />];
		case EligibilityStatus.INELIGIBLE:
			return [palette["seashell"], <Rejected />];
		case FlexFormStatus.REVOKED:
			return [palette["brightGray"], <Revoked />];
		case EligibilityStatus.ELIGIBLE:
			return [palette["cornSilk"], <New />];
		case RefundStatus.INVOICED:
			return [palette["lightGreen"], <Invoiced />];
		case TransactionStatus.IN_REVIEW:
			return [palette["bubbles"], <InReview />];
		case RefundStatus.SETTLED:
			return [palette["lavender"], <Settled />];
		case RefundStatus.DECLINED:
			return [palette["seashell"], <Rejected />];
		case RefundStatus.PENDING:
			return [palette["whiteChocolate"], <Pending />];

		default:
			return [palette["lightGreen"], <Invoiced />];
	}
};

export const getRecordStatusCaseDetailsComponent = (
	recordStatus: FlexFormStatus | NewFlexFormStatus,
	refundStatus: RefundStatus | NewRefundStatus | null | undefined,
	claimEligibilityStatus: string | null | undefined,
	numberOfDeltas: number,
	palette: PaletteOptions,
	t: TFunction
) => {
	const status = getRecordCaseDetailsStatus(recordStatus, refundStatus, claimEligibilityStatus !== EligibilityStatus.INELIGIBLE);
	const [colors, icon] = getRecordStatusCaseDetailsColorsAndIcons(status, palette);
	return (
		<Box display="flex" flexDirection="row" alignItems="center">
			<Box
				display="flex"
				alignItems="center"
				style={{
					borderRadius: "100px",
					backgroundColor: colors.light!,
					padding: "3px 8px 3px 6px",
					marginRight: "4px",
					width: "fit-content",
					maxWidth: "100%",
				}}
			>
				{icon}
				&nbsp;&nbsp;
				<Typography
					type="status"
					style={{
						color: colors.main || "black",
					}}
				>
					<FlexTypographyComponent type="status" text={t(status)} />
				</Typography>
			</Box>
			{numberOfDeltas !== 0 && <Typography>{`+${numberOfDeltas}`}</Typography>}
		</Box>
	);
};

export const getRecordCaseDetailsStatus = (
	recordStatus: FlexFormStatus | NewFlexFormStatus,
	refundStatus: RefundStatus | NewRefundStatus | null | undefined,
	isEligible: boolean
) => {
	if (refundStatus) {
		return transformTransactionStatusNames(refundStatus);
	}

	if (!isEligible) {
		return EligibilityStatus.INELIGIBLE;
	}

	return recordStatus;
};

export const transformTransactionStatusNames = (status: RefundStatus | FlexFormStatus | NewRefundStatus) => {
	switch (status.toUpperCase()) {
		case RefundStatus.NEW:
			return TransactionStatus.ELIGIBLE;
		case RefundStatus.PENDING:
			return TransactionStatus.IN_REVIEW;
		default:
			return status as RefundStatus | FlexFormStatus;
	}
};

export const invoiceStatusToColor = (status: DocumentStatus, palette: Theme["palette"], isText: boolean = false): string => {
	switch (status.toUpperCase()) {
		case DocumentStatus.ISSUED:
			return isText ? palette["lightGreen"].main : palette["lightGreen"].light!;
		case DocumentStatus.WORKING_DRAFT:
			return isText ? palette["cornSilk"].main : palette["cornSilk"].light!;
		case DocumentStatus.SETTLED:
			return isText ? palette["lavender"].main : palette["lavender"].light!;
		case DocumentStatus.IN_REVIEW:
		case DocumentStatus.IN_APPROVAL:
			return isText ? palette["bubbles"].main : palette["bubbles"].light!;
		default:
			console.error("unknown document status");
			return isText ? palette["lavender"].main : palette["lavender"].light!;
	}
};

export const getInvoiceStatusIcon = (state: DocumentStatus): JSX.Element => {
	switch (state) {
		case DocumentStatus.IN_REVIEW:
		case DocumentStatus.IN_APPROVAL:
			return <InReview />;
		case DocumentStatus.ISSUED:
			return <Issued />;
		case DocumentStatus.SETTLED:
			return <Settled />;
		case DocumentStatus.WORKING_DRAFT:
			return <WorkingDraft />;

		default:
			return <Settled />;
	}
};

export const recordStatusToColor = (status: FlexFormStatus, palette: Theme["palette"], isText: boolean = false): string => {
	switch (status.toUpperCase()) {
		case FlexFormStatus.REVOKED:
			return isText ? palette["brightGray"].main : palette["brightGray"].light!;
		case FlexFormStatus.UNMATCHED:
			return isText ? palette["seashell"].main : palette["seashell"].light!;
		case FlexFormStatus.MATCHED:
			return isText ? palette["lightGreen"].main : palette["lightGreen"].light!;
		default:
			console.error("unknown document status");
			return isText ? palette["lavender"].main : palette["lavender"].light!;
	}
};

export const getRecordStatusIcon = (state: FlexFormStatus): JSX.Element => {
	switch (state) {
		case FlexFormStatus.MATCHED:
			return <Issued />;
		case FlexFormStatus.REVOKED:
			return <Suspended />;
		case FlexFormStatus.UNMATCHED:
			return <Rejected />;

		default:
			return <Suspended />;
	}
};

export const publicAgreementStatusToColor = (status: PublicAgreementStatus, palette: Theme["palette"], isText: boolean = false): string => {
	switch (status) {
		case PublicAgreementStatus.ACTIVE:
			return isText ? palette["lavender"].main : palette["lavender"].light!;
		case PublicAgreementStatus.INACTIVE:
			return isText ? palette["brightGray"].main : palette["brightGray"].light!;
		default:
			console.error("unknown status");
			return isText ? palette["brightGray"].main : palette["brightGray"].light!;
	}
};

export const getPublicAgreementStatusIcon = (state: PublicAgreementStatus): JSX.Element => {
	switch (state) {
		case PublicAgreementStatus.ACTIVE:
			return <Running />;
		case PublicAgreementStatus.INACTIVE:
			return <Suspended />;
		default:
			return <Suspended />;
	}
};

export const questStatusToColor = (status: QuestFilterStatus, palette: Theme["palette"], isText: boolean = false): string => {
	switch (status) {
		case QuestFilterStatus.RESOLVED:
			return isText ? palette["lightGreen"].main : palette["lightGreen"].light!;
		case QuestFilterStatus.UNRESOLVED:
			return isText ? palette["seashell"].main : palette["seashell"].light!;
		default:
			console.error("unknown status");
			return isText ? palette["brightGray"].main : palette["brightGray"].light!;
	}
};

export const getQuestStatusIcon = (state: QuestFilterStatus): JSX.Element => {
	switch (state) {
		case QuestFilterStatus.RESOLVED:
			return <Resolved />;
		case QuestFilterStatus.UNRESOLVED:
			return <Unresolved />;
		default:
			return <Suspended />;
	}
};
